import React, { CSSProperties, FC } from 'react';
import Image from 'next/image';
import NavButton from '../../components/navigation/NavButton';
import CallIcon from '../../public/phone-call-footer.svg';
import MailIcon from '../../public/mail-footer.svg';
import LocationIcon from '../../public/map-pin-footer.svg';
import { Input } from '../../components/UIV2/Input';

type FooterLink = { label: string };

const aboutLinks: FooterLink[] = [
  { label: 'About Us' },
  { label: 'Terms of Service' },
  { label: 'Privacy Policy' },
  { label: 'Careers' },
];

const employerLinks: FooterLink[] = [
  { label: 'Post a Job' },
  { label: 'CV Search' },
  { label: 'AI Candidate Screening' },
  { label: 'Screening Questions' },
  { label: 'Screening Chatbot' },
  { label: 'Recruitment Analytics' },
  { label: 'Branded Company Page' },
  { label: 'View Employers' },
];

const jobSeekerLinks: FooterLink[] = [
  { label: 'Premium Jobseekers' },
  { label: 'AutoCV Builder' },
  { label: 'AI Interview Chatbot' },
  { label: 'Apply 4me' },
  { label: 'Job Recommendation' },
  { label: 'Profile Ratings' },
  { label: 'Value My CV' },
];

const jobLinks: FooterLink[] = [
  { label: 'Search Jobs' },
  { label: 'Quick Apply Jobs' },
  { label: 'Foreign Jobs' },
  { label: 'Top Companies' },
];

const columnWidths = {
  about: 135,
  employers: 210,
  jobSeekers: 169,
  jobs: 150,
  contact: 176,
  application: 136,
};

type ColumnStyle = CSSProperties & { '--col-width'?: string };

const createColumnStyle = (width: number): ColumnStyle => ({ '--col-width': `${width}px` });

type ContactInfoItem = {
  key: string;
  icon: any;
  label: string;
  href?: string;
};

const contactInfoItems: ContactInfoItem[] = [
  {
    key: 'phone',
    icon: CallIcon,
    label: '+94 727 360 360',
    href: 'tel:+94727360360',
  },
  {
    key: 'email',
    icon: MailIcon,
    label: 'info@careers360.lk',
    href: 'mailto:info@careers360.lk',
  },
  {
    key: 'address',
    icon: LocationIcon,
    label: '65, Jethawana Road, Colombo 14, Sri Lanka',
  },
];

const contactLinkClass =
  'font-sans font-600 text-base leading-6 text-gray-600 hover:text-primaryRed transition-colors';

export const CandidateUIV2Footer: FC = () => {
  const socialIcons = [
    {
      src: 'https://c.animaapp.com/mibbrmyiIxv710/img/social-icon-2.svg',
      alt: 'Social icon',
      href: 'https://www.linkedin.com',
    },
    {
      src: 'https://c.animaapp.com/mibbrmyiIxv710/img/social-icon.svg',
      alt: 'Social icon',
      href: 'https://www.facebook.com/share/1BxPLGJQWo/?mibextid=wwXIfr',
    },
    {
      src: 'https://c.animaapp.com/mibbrmyiIxv710/img/social-icon-3.svg',
      alt: 'Social icon',
      href: 'https://www.instagram.com/hotj.obs?igsh=NzgwNXphYTgwcWt3',
    },
    {
      src: 'https://c.animaapp.com/mibbrmyiIxv710/img/social-icon-1.svg',
      alt: 'Social icon',
      href: 'https://www.tiktok.com/@hotjobslk?_r=1&_t=ZS-91ub1isDwrQ',
    },
  ];

  const renderLinks = (links: FooterLink[]): JSX.Element => (
    <div className="flex flex-col gap-2">
      {links.map((link) => (
        <NavButton key={link.label} variant="link" className="text-left">
          {link.label}
        </NavButton>
      ))}
    </div>
  );

  return (
    <footer className="flex flex-col w-full items-center gap-10 pt-16 pb-10 bg-white">
      {/* TOP — NEWSLETTER */}
      <div className="flex flex-col w-full max-w-candidate-ui items-start gap-8 px-4 md:px-8">
        <div className="flex flex-col lg:flex-row items-start gap-8 w-full">
          <div className="flex flex-col items-start gap-3 flex-1">
            <div className="font-600 text-[20px] leading-[30px] text-gray-900">
              Join our newsletter
            </div>
            <div className="font-400 text-[16px] leading-[24px] text-gray-600">
              We&apos;ll send you a nice letter once per week. No spam.
            </div>
          </div>

          <div className="inline-flex items-center gap-3 flex-wrap sm:flex-nowrap">
            <Input
              type="email"
              placeholder="Enter your email"
              className="w-[280px] text-gray-500 font-400 text-[16px] leading-[24px] focus:outline-none h-11 px-3.5 py-2.5 bg-white rounded-lg border border-solid border-[#d5d6da] shadow-shadow-xs font-text-md-regular tracking-[var(--text-md-regular-letter-spacing)] [font-style:var(--text-md-regular-font-style)]"
            />
            <NavButton variant="primary" size="sm" className="!h-11 !px-5">
              Subscribe
            </NavButton>
          </div>
        </div>
      </div>

      {/* FOOTER COLUMNS */}
      <div className="relative w-full max-w-candidate-ui px-4 md:px-6">
        <div className="flex flex-col gap-6 sm:grid sm:grid-cols-3 sm:gap-6 lg:flex lg:flex-row lg:flex-wrap lg:justify-between lg:gap-8">
          <div
            className="flex flex-col gap-3 w-full lg:flex-none lg:w-[var(--col-width)]"
            style={createColumnStyle(columnWidths.about)}>
            <div className="text-left font-600 text-[14px] leading-[20px] text-gray-500">
              About HotJobs
            </div>
            {renderLinks(aboutLinks)}
          </div>

          <div
            className="flex flex-col gap-3 w-full lg:flex-none lg:w-[var(--col-width)]"
            style={createColumnStyle(columnWidths.employers)}>
            <div className="text-left font-600 text-[14px] leading-[20px] text-gray-500">
              Employers
            </div>
            {renderLinks(employerLinks)}
          </div>

          <div
            className="flex flex-col gap-3 w-full lg:flex-none lg:w-[var(--col-width)]"
            style={createColumnStyle(columnWidths.jobSeekers)}>
            <div className="text-left font-600 text-[14px] leading-[20px] text-gray-500">
              Job Seekers
            </div>
            {renderLinks(jobSeekerLinks)}
          </div>

          <div
            className="flex flex-col gap-3 w-full lg:flex-none lg:w-[var(--col-width)]"
            style={createColumnStyle(columnWidths.jobs)}>
            <div className="text-left font-600 text-[14px] leading-[20px] text-gray-500">Jobs</div>
            {renderLinks(jobLinks)}
          </div>

          <div
            className="flex flex-col gap-3 w-full lg:flex-none lg:w-[var(--col-width)]"
            style={createColumnStyle(columnWidths.contact)}>
            <div className="text-left font-600 text-[14px] leading-[20px] text-gray-500">
              Contact Us
            </div>

            <div className="flex flex-col gap-4">
              {contactInfoItems.map(({ key, icon, label, href }) => (
                <div key={key} className="flex items-start gap-3">
                  <Image src={icon} alt={`${key} icon`} width={20} height={20} />
                  {href ? (
                    <a href={href} className={contactLinkClass}>
                      {label}
                    </a>
                  ) : (
                    <span className={contactLinkClass}>{label}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* APPLICATION COLUMN (RIGHT ALIGNED) */}
          <div
            className="flex flex-col gap-3 w-full lg:flex-none lg:w-[var(--col-width)] lg:text-right"
            style={createColumnStyle(columnWidths.application)}>
            <div className="text-left font-600 text-[14px] leading-[20px] text-gray-500 ">
              Application
            </div>

            <div className="flex flex-col items-start lg:items-end gap-4 mt-2">
              <img
                className="w-[136px] h-[41px] object-contain"
                alt="Google Play"
                src="https://c.animaapp.com/mibbrmyiIxv710/img/image-2.png"
              />
              <img
                className="w-[136px] h-[41px] object-contain"
                alt="App Store"
                src="https://c.animaapp.com/mibbrmyiIxv710/img/image-1.png"
              />
            </div>
          </div>

          {/* WHATSAPP FLOAT */}
          <div className="absolute bottom-0 right-6">
            <img
              className="w-[45px] h-[45px] object-cover"
              alt="WhatsApp"
              src="https://c.animaapp.com/mibbrmyiIxv710/img/-pngtree-whatsapp-phone-icon-vector-8704826-1.png"
            />
          </div>
        </div>
      </div>

      {/* BOTTOM SECTION */}
      <div className="w-full max-w-candidate-ui px-6">
        <div className="w-full h-[1px] bg-gray-200" />

        <div className="flex flex-col gap-16 w-full">
          <div className="flex items-center justify-between w-full flex-wrap gap-8">
            {/* Powered by + Copyright */}
            <div className="flex items-center gap-[198px] flex-wrap">
              <div className="flex flex-col">
                <p className="font-400 text-[14px] leading-[20px] text-gray-500">Powered by</p>

                <div className="inline-flex items-center gap-5 flex-wrap translate-y-[-18px]">
                  <img
                    className="w-[159px] h-9 object-cover"
                    src="https://c.animaapp.com/mibbrmyiIxv710/img/image-3.png"
                    alt="Partner 1"
                  />
                  <img
                    className="w-[142px] h-[53px] object-cover"
                    src="https://c.animaapp.com/mibbrmyiIxv710/img/image-4.png"
                    alt="Partner 2"
                  />
                </div>
              </div>

              <p className="font-400 text-[16px] leading-[24px] text-gray-500 whitespace-nowrap">
                © 2025 HotJobs. All rights reserved.
              </p>
            </div>

            {/* Social Icons */}
            <div className="inline-flex items-center gap-6">
              {socialIcons.map((icon, i) => (
                <a
                  key={i}
                  href={icon.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:opacity-80 transition-opacity cursor-pointer"
                  aria-label={`Visit our ${icon.alt}`}>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img className="w-6 h-6 pointer-events-none" src={icon.src} alt={icon.alt} />
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
